import { useCallback, useEffect, useState } from 'react';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

/**
 * L'evento "beforeinstallprompt" è un'API proprietaria dei browser basati su
 * Chromium (Chrome, Edge, Opera, Samsung Internet, Brave...): Firefox non
 * l'ha mai implementata, Safari desktop nemmeno. Su questi browser il
 * pulsante nativo non comparirà mai, quindi lo rileviamo qui invece di
 * aspettare un evento che non arriverà.
 */
function isInstallCapableBrowser() {
  if (isIos()) return false; // su iOS nessun browser espone l'evento, gestito a parte
  const ua = window.navigator.userAgent;
  return /Chrome|Chromium|Edg\/|OPR\/|SamsungBrowser/i.test(ua);
}

/**
 * Gestisce l'installazione della PWA come app: intercetta l'evento
 * "beforeinstallprompt" (Chrome/Edge/Android) e lo tiene pronto finché
 * l'utente non clicca il pulsante "Installa l'app". Su iOS Safari, che non
 * supporta questo evento, segnala che serve la procedura manuale
 * (Condividi -> Aggiungi alla schermata Home). Sugli altri browser che non
 * lo supportano affatto (Firefox, Safari desktop), consiglia di aprire la
 * pagina con Chrome o Edge.
 */
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice.outcome;
  }, [deferredPrompt]);

  const iosManual = !installed && isIos();
  const otherBrowser = !installed && !iosManual && !isInstallCapableBrowser();

  return {
    installed,
    canInstall: !installed && !!deferredPrompt,
    isIosManualInstall: iosManual && !deferredPrompt,
    suggestOtherBrowser: otherBrowser,
    promptInstall,
  };
}
